import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import {
  KanbanCard,
  KanbanCardDto,
  KanbanFilterBadge,
  KanbanFilterBadgeDto,
  KanbanColumnSort,
  KanbanColumnSortFieldOptions,
  KanbanFieldFilterValue,
  KanbanFilters,
  KanbanFunnel,
  KanbanGetBoardRequest,
  KanbanGetBoardResponse,
  KanbanGetColumnItemsRequest,
  KanbanGetColumnItemsResponse,
  KanbanMoveCardRequest,
  KanbanMoveCardResponse,
  StatusId,
} from '../store/kanban.types'
import {
  appendKanbanFieldFiltersToSearchParams,
  normalizeKanbanFieldFilters,
} from '../lib/kanbanFieldFilters'
import { CrmFunnelsIndexResponse } from '@/api/types/crm/crmFunnels.types'
import { mapLeadsToTableRows } from '@pages/Admin/CRM/utils/mapLeadsToTableRows'
import { normalizeCrmLeadTaskCounts } from '@pages/Admin/CRM/utils/crmLeadTaskCounts'
import { mapKanbanFunnelLeadListItemToCrm } from '../lib/mapKanbanFunnelLeadListItemToCrm'

type ApiEnvelope<T> = {
  status: 'success'
  data: T
}

type ApiErrorEnvelope = {
  status: 'error'
  error?: string
  message?: string
}

type ApiKanbanColumn = {
  status_id: number
  status_name: string
  color: string
  total_count: number
  items: KanbanCardDto[]
  next_cursor: string | null
  has_more: boolean
}

type ApiGetBoardData = {
  funnel: {
    id: number
    name: string
  }
  columns: ApiKanbanColumn[]
  funnels?: { id: number; unassigned_count: number }[]
}

type ApiGetColumnItemsData = {
  status_id: number
  items: KanbanCardDto[]
  next_cursor: string | null
  has_more: boolean
  total_count: number
}

type ApiMoveCardData = {
  lead_funnel_id: number
  status_id: number
  sorting: number
  updated_at: string
}

type ApiGetFunnelsData = CrmFunnelsIndexResponse['data']

export type KanbanApiFilters = {
  search?: string | null
  manager_id?: number | null
  next_contact_date_from?: string | null
  next_contact_date_to?: string | null
  last_activity_date_from?: string | null
  last_activity_date_to?: string | null
  source?: string | null
  overdue_only?: 0 | 1
  tasks_overdue_from?: number
  tasks_today_from?: number
  sort_field?: (typeof KanbanColumnSortFieldOptions)[number]['value'] | null
  sort_order?: 'asc' | 'desc' | null
  column_sort?: Record<
    string,
    {
      sort_field: (typeof KanbanColumnSortFieldOptions)[number]['value']
      sort_order: 'asc' | 'desc'
    }
  > | null
  field_filters?: Record<string, KanbanFieldFilterValue>
}

const KANBAN_SEARCH_MAX_LENGTH = 255

const normalizeKanbanSearchForApi = (
  search: string | null | undefined,
): string | undefined => {
  const trimmed = search?.trim() ?? ''
  if (!trimmed) return undefined
  return trimmed.slice(0, KANBAN_SEARCH_MAX_LENGTH)
}

const mapFiltersToApi = (filters?: KanbanFilters): KanbanApiFilters => {
  const api: KanbanApiFilters = {
    manager_id: filters?.managerId,
    next_contact_date_from: filters?.nextContactDateFrom,
    next_contact_date_to: filters?.nextContactDateTo,
    last_activity_date_from: filters?.lastActivityDateFrom,
    last_activity_date_to: filters?.lastActivityDateTo,
    source: filters?.source,
  }
  const search = normalizeKanbanSearchForApi(filters?.search)
  if (search) {
    api.search = search
  }
  if (filters?.overdueOnly === true) {
    api.overdue_only = 1
  }
  if (
    filters?.tasksOverdueFrom != null &&
    Number.isFinite(Number(filters.tasksOverdueFrom)) &&
    Number(filters.tasksOverdueFrom) >= 0
  ) {
    api.tasks_overdue_from = Math.trunc(Number(filters.tasksOverdueFrom))
  }
  if (
    filters?.tasksTodayFrom != null &&
    Number.isFinite(Number(filters.tasksTodayFrom)) &&
    Number(filters.tasksTodayFrom) >= 0
  ) {
    api.tasks_today_from = Math.trunc(Number(filters.tasksTodayFrom))
  }
  const field_filters = normalizeKanbanFieldFilters(filters?.fieldFilters)
  if (field_filters) {
    api.field_filters = field_filters
  }
  return api
}

export function mergeKanbanFiltersForApi(
  filters: KanbanFilters | undefined,
  columnSortByStatusId:
    | Record<StatusId, KanbanColumnSort>
    | undefined
    | null,
): KanbanApiFilters {
  const api = mapFiltersToApi(filters)

  if (!columnSortByStatusId) {
    return api
  }

  /** Только явная пользовательская сортировка. `none` + включённый свитч = дефолт репозитория (applyDefaultNextContactOrder), ключ в column_sort не шлём. */
  const column_sort: NonNullable<KanbanApiFilters['column_sort']> = {}
  for (const [idStr, cfg] of Object.entries(columnSortByStatusId)) {
    if (!cfg?.enabled) continue
    if (cfg.sortField === 'none') continue
    const sid = Number(idStr)
    if (!Number.isFinite(sid)) continue
    column_sort[String(sid)] = {
      sort_field: cfg.sortField,
      sort_order: cfg.sortOrder,
    }
  }
  if (Object.keys(column_sort).length > 0) {
    api.column_sort = column_sort
  }
  return api
}

const mapFilterBadgeFromApi = (badge: KanbanFilterBadgeDto): KanbanFilterBadge => ({
  fieldId: badge.field_id,
  fieldName: badge.field_name,
  type: badge.type,
  value: badge.value,
  ...(badge.raw_value != null ? { rawValue: badge.raw_value } : {}),
  ...(badge.file_name != null && String(badge.file_name).trim()
    ? { fileName: String(badge.file_name).trim() }
    : {}),
  ...(badge.file_url != null && String(badge.file_url).trim()
    ? { fileUrl: String(badge.file_url).trim() }
    : {}),
  ...(badge.color != null && String(badge.color).trim()
    ? { color: String(badge.color).trim() }
    : {}),
})

const mapCardFromApi = (card: KanbanCardDto): KanbanCard => {
  let funnelListRow = undefined as KanbanCard['funnelListRow']
  if (card.funnel_lead_list_item) {
    try {
      const crmItem = mapKanbanFunnelLeadListItemToCrm(
        card.funnel_lead_list_item,
      )
      funnelListRow = mapLeadsToTableRows([crmItem])[0]
    } catch {
      funnelListRow = undefined
    }
  }

  return {
    leadFunnelId: card.lead_funnel_id,
    leadId: card.lead_id,
    funnelId: card.funnel_id,
    statusId: card.status_id,
    managerId: card.manager_id,
    source: card.source,
    name: card.name,
    phone: card.phone,
    nextContactAt: card.next_contact_at,
    lastActivityAt:
      card.last_activity_at !== undefined && card.last_activity_at !== null
        ? card.last_activity_at
        : undefined,
    updatedAt: card.updated_at,
    sorting: card.sorting,
    funnelListRow,
    vk: card.funnel_lead_list_item?.vk,
    integrations: card.funnel_lead_list_item?.integrations,
    filterBadges: (card.filter_badges ?? []).map(mapFilterBadgeFromApi),
    taskCounts: normalizeCrmLeadTaskCounts(card.task_counts),
  }
}

const mapFunnelFromApi = (
  funnel: ApiGetFunnelsData['funnels'][number],
): KanbanFunnel => ({
  id: funnel.id,
  name: funnel.name,
  sorting: funnel.sorting,
})

const mapFunnelUnassignedCountsFromApi = (
  rows: ApiGetBoardData['funnels'],
): KanbanGetBoardResponse['funnels'] =>
  (rows ?? []).map((row) => ({
    id: Number(row.id),
    unassigned_count: Math.max(0, Number(row.unassigned_count) || 0),
  }))

export const kanbanApi = {
  getAllFunnels: async (): Promise<KanbanFunnel[]> => {
    const response = await crmApi.funnels.getAllFunnels()
    if (isError(response)) {
      throw new Error('Не удалось загрузить воронки')
    }

    const payload = response.data as CrmFunnelsIndexResponse

    return payload.data.funnels
      .map(mapFunnelFromApi)
      .sort((a, b) => a.sorting - b.sorting)
  },

  getBoardInit: async ({
    funnelId,
    filters,
    columnsLimit,
    statusLimits,
    columnSortByStatusId,
  }: KanbanGetBoardRequest): Promise<KanbanGetBoardResponse> => {
    const response = await crmApi.kanban.getBoard({
      funnel_id: funnelId,
      filters: mergeKanbanFiltersForApi(filters, columnSortByStatusId),
      columns_limit: columnsLimit,
      status_limits: statusLimits,
    })

    const payload = response.data as
      | ApiEnvelope<ApiGetBoardData>
      | ApiErrorEnvelope

    if (payload.status !== 'success') {
      throw new Error(
        payload.error ?? payload.message ?? 'Не удалось загрузить канбан',
      )
    }

    return {
      funnel: payload.data.funnel,
      columns: payload.data.columns.map((column) => ({
        statusId: column.status_id,
        statusName: column.status_name,
        statusColor: column.color,
        total: column.total_count,
        items: column.items.map(mapCardFromApi),
        nextCursor: column.next_cursor,
        hasMore: column.has_more,
      })),
      funnels: mapFunnelUnassignedCountsFromApi(payload.data.funnels),
    }
  },

  /** Тот же board, без карточек в колонках — только `funnels[].unassigned_count`. */
  getFunnelUnassignedCounts: async (
    request: Omit<KanbanGetBoardRequest, 'columnsLimit'>,
  ): Promise<KanbanGetBoardResponse['funnels']> => {
    const board = await kanbanApi.getBoardInit({
      ...request,
      columnsLimit: 1,
    })
    return board.funnels ?? []
  },

  moveCard: async ({
    leadFunnelId,
    toStatusId,
    sorting,
  }: KanbanMoveCardRequest): Promise<KanbanMoveCardResponse> => {
    const response = await crmApi.kanban.moveCard({
      lead_funnel_id: leadFunnelId,
      to_status_id: toStatusId,
      sorting: sorting,
    })
    const payload = response.data as
      | ApiEnvelope<ApiMoveCardData>
      | ApiErrorEnvelope

    if (payload.status !== 'success') {
      throw new Error(
        payload.error ?? payload.message ?? 'Не удалось переместить карточку',
      )
    }

    return {
      leadFunnelId: payload.data.lead_funnel_id,
      statusId: payload.data.status_id,
      sorting: payload.data.sorting,
      updatedAt: payload.data.updated_at,
    }
  },

  getMoreColumnItems: async ({
    funnelId,
    statusId,
    cursor,
    limit,
    filters,
    columnSortByStatusId,
  }: KanbanGetColumnItemsRequest): Promise<KanbanGetColumnItemsResponse> => {
    const response = await crmApi.kanban.getMoreColumnItems({
      funnel_id: funnelId,
      status_id: statusId,
      cursor,
      limit,
      filters: mergeKanbanFiltersForApi(filters, columnSortByStatusId),
    })
    const payload = response.data as
      | ApiEnvelope<ApiGetColumnItemsData>
      | ApiErrorEnvelope

    if (payload.status !== 'success') {
      throw new Error(
        payload.error ?? payload.message ?? 'Не удалось догрузить колонку',
      )
    }

    return {
      statusId: payload.data.status_id,
      items: payload.data.items.map(mapCardFromApi),
      nextCursor: payload.data.next_cursor,
      hasMore: payload.data.has_more,
      total: payload.data.total_count,
    }
  },
}

/** Пишет filters[...] в query: плоские поля + вложенный column_sort для бэка Yii. */
export function appendKanbanApiFiltersToSearchParams(
  query: URLSearchParams,
  filters: KanbanApiFilters,
) {
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return

    if (key === 'column_sort') {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return
      }
      Object.entries(value).forEach(([statusId, entry]) => {
        if (!entry || typeof entry !== 'object') return
        const sf = entry.sort_field
        if (sf === undefined || sf === null || String(sf) === '') return
        query.set(
          `filters[column_sort][${statusId}][sort_field]`,
          String(sf),
        )
        query.set(
          `filters[column_sort][${statusId}][sort_order]`,
          String(entry.sort_order),
        )
      })
      return
    }

    if (key === 'field_filters') {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return
      }
      appendKanbanFieldFiltersToSearchParams(
        query,
        value as Record<string, KanbanFieldFilterValue>,
      )
      return
    }

    query.set(`filters[${key}]`, String(value))
  })
}

export const buildKanbanBoardQueryString = (params: {
  funnel_id: number
  filters?: KanbanApiFilters
  columns_limit?: number
  status_limits?: Record<number, number>
}) => {
  const query = new URLSearchParams()
  query.set('funnel_id', String(params.funnel_id))

  if (typeof params.columns_limit === 'number') {
    query.set('columns_limit', String(params.columns_limit))
  }

  if (params.status_limits) {
    Object.entries(params.status_limits).forEach(([statusId, limit]) => {
      const safeStatusId = Number(statusId)
      const safeLimit = Number(limit)
      if (!Number.isFinite(safeStatusId) || safeStatusId <= 0) return
      if (!Number.isFinite(safeLimit) || safeLimit <= 0) return
      query.set(`status_limits[${safeStatusId}]`, String(safeLimit))
    })
  }

  if (params.filters) {
    appendKanbanApiFiltersToSearchParams(query, params.filters)
  }

  return query.toString()
}

export const buildKanbanColumnItemsQueryString = (params: {
  funnel_id: number
  status_id: number
  cursor?: string | null
  limit?: number
  filters?: KanbanApiFilters
}) => {
  const query = new URLSearchParams()
  query.set('funnel_id', String(params.funnel_id))
  query.set('status_id', String(params.status_id))

  if (
    params.cursor !== undefined &&
    params.cursor !== null &&
    params.cursor !== ''
  ) {
    query.set('cursor', String(params.cursor))
  }

  if (typeof params.limit === 'number') {
    query.set('limit', String(params.limit))
  }

  if (params.filters) {
    appendKanbanApiFiltersToSearchParams(query, params.filters)
  }

  return query.toString()
}
